import React, { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import PageHeader from '@/components/Common/PageHeader'
import SEOHead from '@/components/SEO/SEOHead'
import { buildGraph, breadcrumbListData, itemListSchema } from '@/data/structured-data'
import { dogDiseases, catDiseases } from '@/data/diseases'
import { motion } from 'framer-motion'
import { ArrowRight, AlertTriangle } from 'lucide-react'

const DiseaseCategory: React.FC = () => {
  const { category } = useParams()
  const isDog = category === 'kopek'
  const categorySlug = isDog ? 'kopek' : 'kedi'
  const categoryLabel = isDog ? 'Köpek Hastalıkları' : 'Kedi Hastalıkları'
  const rawList = isDog ? dogDiseases : catDiseases
  const [query, setQuery] = useState('')
  const list = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rawList
    return rawList.filter(d =>
      d.title.toLowerCase().includes(q) || d.content.toLowerCase().includes(q)
    )
  }, [rawList, query])

  const excerpt = (text: string, len = 140) => {
    if (text.length <= len) return text
    const cut = text.slice(0, len)
    const lastSpace = cut.lastIndexOf(' ')
    return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut) + '…'
  }

  return (
    <>
      <SEOHead
        title={`${categoryLabel} | Ardıç Veteriner Kliniği`}
        description={`${isDog ? 'Köpek' : 'Kedi'} hastalıkları rehberi: ${list.length} hastalığın belirtileri, tedavi yaklaşımları ve korunma yolları. Şanlıurfa Karaköprü Ardıç Veteriner Kliniği tarafından hazırlandı.`}
        keywords={isDog ? 'köpek hastalıkları, köpek aşı takvimi, köpek kısırlaştırma' : 'kedi hastalıkları, kedi iç parazit, kedi kısırlaştırma fiyat'}
        canonicalUrl={`/hastaliklari/${categorySlug}`}
        structuredData={buildGraph(
          itemListSchema({
            name: categoryLabel,
            url: `/hastaliklari/${categorySlug}`,
            items: rawList.map(d => ({ name: d.title, url: `/hastaliklari/${categorySlug}/${d.slug}` }))
          }),
          breadcrumbListData([
            { name: 'Ana Sayfa', url: '/' },
            { name: 'Hastalık Rehberi', url: '/hastaliklari' },
            { name: categoryLabel, url: `/hastaliklari/${categorySlug}` }
          ])
        )}
      />
      <PageHeader title={categoryLabel} />
      <section className="section-padding">
        <div className="container-custom">
          {/* Search */}
          <div className="mb-6 flex items-center gap-3">
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Hastalık ara…"
              className="w-full md:w-96 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((d, i) => (
              <motion.div
                key={d.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                whileHover={{ y: -6 }}
                className="relative group bg-white rounded-2xl border p-6 shadow overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-transparent to-accent-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className="relative z-10">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-primary-100">
                      <AlertTriangle className="w-5 h-5 text-primary-700" />
                    </div>
                    <h3 className="text-base font-bold text-gray-900">{d.title}</h3>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    {excerpt(d.content)}
                  </p>

                  {d.symptoms && d.symptoms.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-5">
                      {d.symptoms.slice(0, 3).map((s, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 rounded-full bg-primary-50 text-primary-700 text-xs border border-primary-200"
                        >
                          {s}
                        </span>
                      ))}
                      {d.symptoms.length > 3 && (
                        <span className="px-2 py-1 rounded-full bg-gray-50 text-gray-600 text-xs border">+{d.symptoms.length - 3}</span>
                      )}
                    </div>
                  )}

                  <Link
                    to={`/hastaliklari/${category}/${d.slug}`}
                    className="inline-flex items-center gap-2 font-semibold text-primary-700 group/link"
                  >
                    Detayları Gör
                    <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover/link:translate-x-1" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}

export default DiseaseCategory


